import { createSlice } from '@reduxjs/toolkit';
import { deriveDeviceStateFromFields } from 'library/helpers/deviceFieldState';
import { activityInitialState, activityReducers } from '../_shared/activityState';

/**
 * Devices slice — scoped by org + site.
 *
 * Data structure:
 *   state.data[orgID][siteID] = { data: [...], loading: false }
 *
 * Three list-load actions prevent saga loops:
 *   loadDevices       → trigger (saga listens, reducer is no-op)
 *   loadDevicesStart   → sets bucket loading (no saga listens)
 *   loadDevicesSuccess → replaces bucket data
 */

const initialState = {
	activity: { ...activityInitialState },
	// Scoped: data[orgID][siteID] = { data: [], loading: false }
	data: {},
	error: null,

	// ✅ Devices Loadings (global — CRUD transient state)
	createDeviceLoading: false,
	updateDeviceLoading: false,
	deleteDeviceLoading: false,

	// ✅ Devices Fields Loadings
	loadDeviceFieldsLoading: false,
	createDeviceFieldLoading: false,
	updateDeviceFieldLoading: false,
	deleteDeviceFieldLoading: false,

	// ✅ Devices Location Loadings
	loadDeviceLocationLoading: false,
	createDeviceLocationLoading: false,
	updateDeviceLocationLoading: false,
	deleteDeviceLocationLoading: false,
	upsertDeviceLocationLoading: false,

	// ✅ Devices Helpers Loadings
	actionDevicesLoading: false,
	overrideDeviceLoading: false,
	// Per-door progress for a lock/unlock fan-out. The backend actions ONE device per request, so the
	// modal renders a row per target and fills it in as each request settles — that is what makes
	// "which locks opened" answerable, and what a per-row retry needs.
	//
	// `detached` = the modal that fired this run has closed; the run now belongs to the notifications
	// popover (`BackgroundJobs`) and no modal may re-adopt it. See `detachOverrideProgress`.
	overrideProgress: { active: false, actionID: null, rows: [], detached: false },

	// ✅ Live check — POST /devices/:deviceID/check-live. One vendor round trip answers both
	// questions, because for a live provider the battery reading IS the proof of life.
	// Keyed by deviceID; absent means nobody has checked this device this session.
	liveByDeviceID: {}, // { [deviceID]: { online, battery, checkedAt, source } }
	liveLoadingByDeviceID: {},
	// ✅ Connectivity history for ONE device — the day buckets its strip draws and the raw
	// check timeline its drawer lists, from one read. Same shape the Gateways slice holds for
	// a gateway, deliberately: it is the same table answering the same question one level down.
	historyByDeviceID: {} // { [deviceID]: { loading, recordsRequested, buckets, records, nextBefore } }
};

const historyAt = (state, deviceID) => state.historyByDeviceID[deviceID] || {};

// Ensures the bucket exists at data[orgID][key]
//
// `locationsLoaded` is the SECOND half of a device row and it has its own flag because it
// arrives in its own request: /devices answers first, then getAllDevicesAllLocations
// patches `location` + `sites` onto every row. Between the two a device is real but
// unplaced, and anything that reads placement — the floor plan, and the Cameras page,
// which derives its whole camera list from this bucket — would report every row as
// unallocated. `loading` cannot say this: it is already false by then.
const ensureBucket = (state, orgID, siteID) => {
	const key = siteID;
	if (!state.data[orgID]) state.data[orgID] = {};
	if (!state.data[orgID][key]) state.data[orgID][key] = { data: [], loading: false, locationsLoaded: false };
	return key;
};

// Patches a single device inside the active scope bucket
const patchDevice = (state, orgID, siteID, deviceID, patcher) => {
	const key = siteID;
	const bucket = state.data[orgID]?.[key];
	if (!bucket?.data) return;
	bucket.data = bucket.data.map((d) => (d.deviceID === deviceID ? patcher(d) : d));
};

/**
 * Patch a device in EVERY bucket of the org that holds it.
 *
 * 🛑 The active-bucket-only patch silently loses writes. `selectDevicesByDeviceID` falls back
 * to the 'all' bucket when the active site bucket has no such row — so a drawer opened from
 * search, the portfolio or a floor plan renders the 'all' copy while a patch keyed on the
 * selected siteID lands on a bucket nothing is reading. The value saves, the server has it,
 * and the row still shows the old one.
 */
const patchDeviceEverywhere = (state, orgID, deviceID, patcher) => {
	const buckets = state.data[orgID];
	if (!buckets) return;
	Object.values(buckets).forEach((bucket) => {
		if (!bucket?.data) return;
		bucket.data = bucket.data.map((d) => (d.deviceID === deviceID ? patcher(d) : d));
	});
};

const devicesSlice = createSlice({
	name: 'devices',
	initialState,
	reducers: {
		...activityReducers,
		// ✅ Devices Actions (scoped)

		// Trigger — saga listens (takeLatest). Reducer is a no-op.
		loadDevices: () => {},

		// Sets loading on the target bucket. No saga listens (prevents loop).
		loadDevicesStart: (state, action) => {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
		},

		// Replaces bucket data — backend is source of truth for this scope.
		loadDevicesSuccess: (state, action) => {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			// `locationsLoaded: false` — these rows are unplaced until getAllDevicesLocations,
			// which getDevicesSuccess fires next.
			state.data[orgID][key] = { data, loading: false, loaded: true, locationsLoaded: false };
		},

		loadDevicesError: (state, action) => {
			const { orgID, siteID, error } = action.payload;
			const key = siteID;
			if (state.data[orgID]?.[key]) state.data[orgID][key].loading = false;
			state.error = error;
		},

		// REFRESH (silent — replaces data without loading flash, used after CRUD operations)
		refreshDevices: () => {},
		refreshDevicesSuccess: (state, action) => {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].data = data;
			// 🛑 `locationsLoaded` is deliberately NOT reset. This is the SILENT refresh — its
			// whole point is that a CRUD operation does not flash a loading state over a page
			// that is already showing data. The locations patch follows immediately (see
			// getDevicesSuccess) and re-places the rows.
		},

		// Patches location + sites onto each device in the active bucket
		loadAllDevicesLocations: (state) => {
			state.error = null;
		},
		loadAllDevicesLocationsSuccess: (state, action) => {
			const { orgID, siteID, data } = action.payload;
			const key = siteID;
			const bucket = state.data[orgID]?.[key];
			if (!bucket?.data) return;

			bucket.locationsLoaded = true;
			bucket.data = bucket.data.map((device) => {
				const allRows = data[device.deviceID] || [];
				const location = allRows.filter((row) => row.floorID !== null);
				const sites = allRows.filter((row) => row.siteID !== null).map((row) => ({ siteID: row.siteID }));
				// Deduplicate sites (a device on a floor also has siteID)
				const uniqueSites = [...new Map(sites.map((s) => [s.siteID, s])).values()];
				return { ...device, location: location.length ? location : null, sites: uniqueSites };
			});
		},
		loadAllDevicesLocationsError: (state, action) => {
			state.error = action.payload;
		},

		// ✅ CRUD actions (global loading flags)

		createDevice: (state) => {
			state.createDeviceLoading = true;
			state.error = null;
		},
		createDeviceSuccess: (state) => {
			state.createDeviceLoading = false;
		},
		createDeviceError: (state, action) => {
			state.createDeviceLoading = false;
			state.error = action.payload;
		},

		createMultipleDevices: (state) => {
			state.createDeviceLoading = true;
			state.error = null;
		},
		createMultipleDevicesSuccess: (state) => {
			state.createDeviceLoading = false;
		},
		createMultipleDevicesError: (state, action) => {
			state.createDeviceLoading = false;
			state.error = action.payload;
		},

		updateDevice: (state) => {
			state.updateDeviceLoading = true;
		},
		updateDeviceSuccess: (state) => {
			state.updateDeviceLoading = false;
		},
		updateDeviceError: (state, action) => {
			state.updateDeviceLoading = false;
			state.error = action.payload;
		},

		// Delete device — the modal closes optimistically on dispatch; this flag only
		// drives the topbar in-flight indicator, so no success flag is needed.
		deleteDevice: (state) => {
			state.deleteDeviceLoading = true;
		},
		deleteDeviceSuccess: (state) => {
			state.deleteDeviceLoading = false;
		},
		deleteDeviceError: (state, action) => {
			state.deleteDeviceLoading = false;
			state.error = action.payload;
		},

		// ✅ Devices Fields Actions — patches device inside active bucket
		loadDeviceFields: (state) => {
			state.loadDeviceFieldsLoading = true;
			state.error = null;
		},
		loadDeviceFieldsSuccess: (state, action) => {
			// `siteID` rides on the payload and is deliberately unused: the patch below covers
			// every bucket, so scoping it to the selected site would be the bug it fixes.
			const { orgID, deviceID, fields } = action.payload;
			// 🛑 `status` and `battery` are PROMOTED out of these fields by /devices, and this read
			// answers the fields alone — so re-derive the pair here or the row keeps rendering the
			// status it was listed with while its own fields say otherwise. That contradiction is
			// exactly what a live check produces: the check rewrites the fields, we re-read them,
			// and the badge would still show the reading from before the check.
			const derived = Array.isArray(fields) ? deriveDeviceStateFromFields(fields) : null;
			// Every copy, not just the active bucket — see patchDeviceEverywhere.
			patchDeviceEverywhere(state, orgID, deviceID, (d) => ({ ...d, fields, ...(derived || {}) }));
			state.loadDeviceFieldsLoading = false;
		},
		loadDeviceFieldsError: (state, action) => {
			state.loadDeviceFieldsLoading = false;
			state.error = action.payload;
		},

		createDeviceField: (state) => {
			state.createDeviceFieldLoading = true;
		},
		createDeviceFieldSuccess: (state) => {
			state.createDeviceFieldLoading = false;
		},
		createDeviceFieldError: (state, action) => {
			state.createDeviceFieldLoading = false;
			state.error = action.payload;
		},
		updateDeviceField: (state) => {
			state.updateDeviceFieldLoading = true;
		},
		updateDeviceFieldSuccess: (state) => {
			state.updateDeviceFieldLoading = false;
		},
		updateDeviceFieldError: (state, action) => {
			state.updateDeviceFieldLoading = false;
			state.error = action.payload;
		},

		deleteDeviceField: (state) => {
			state.deleteDeviceFieldLoading = true;
		},
		deleteDeviceFieldSuccess: (state) => {
			state.deleteDeviceFieldLoading = false;
		},
		deleteDeviceFieldError: (state, action) => {
			state.deleteDeviceFieldLoading = false;
			state.error = action.payload;
		},

		// ✅ Devices Location Actions — patches device inside active bucket
		loadDeviceLocation: (state) => {
			state.loadDeviceLocationLoading = true;
		},
		loadDeviceLocationSuccess: (state, action) => {
			const { orgID, siteID, deviceID, location } = action.payload;
			patchDevice(state, orgID, siteID, deviceID, (d) => ({ ...d, location }));
			state.loadDeviceLocationLoading = false;
		},
		loadDeviceLocationError: (state, action) => {
			state.loadDeviceLocationLoading = false;
			state.error = action.payload;
		},
		createDeviceLocation: (state) => {
			state.createDeviceLocationLoading = true;
			state.error = null; // Clear any previous errors
		},
		createDeviceLocationSuccess: (state) => {
			state.createDeviceLocationLoading = false;
		},
		createDeviceLocationError: (state, action) => {
			state.createDeviceLocationLoading = false;
			state.error = action.payload;
		},
		updateDeviceLocation: (state) => {
			state.updateDeviceLocationLoading = true;
			state.error = null; // Clear any previous errors
		},
		updateDeviceLocationSuccess: (state) => {
			state.updateDeviceLocationLoading = false;
		},
		updateDeviceLocationError: (state, action) => {
			state.updateDeviceLocationLoading = false;
			state.error = action.payload;
		},
		deleteDeviceLocation: (state) => {
			state.deleteDeviceLocationLoading = true;
		},
		deleteDeviceLocationSuccess: (state, action) => {
			state.deleteDeviceLocationLoading = false;
			const { orgID, siteID, deviceID } = action.payload;
			patchDevice(state, orgID, siteID, deviceID, (d) => ({ ...d, location: null }));
		},
		deleteDeviceLocationError: (state, action) => {
			state.deleteDeviceLocationLoading = false;
			state.error = action.payload;
		},

		// ✅ Move Device Location (compound: ensure site → delete old → create new)
		upsertDeviceLocation: (state) => {
			state.upsertDeviceLocationLoading = true;
			state.error = null;
		},
		upsertDeviceLocationSuccess: (state) => {
			state.upsertDeviceLocationLoading = false;
		},
		upsertDeviceLocationError: (state, action) => {
			state.upsertDeviceLocationLoading = false;
			state.error = action.payload;
		},

		// ✅ Device Sites Actions
		updateDeviceSites: (state) => {
			state.loadingUpdateSites = true;
			state.error = null;
		},
		updateDeviceSitesSuccess: (state) => {
			state.loadingUpdateSites = false;
		},
		updateDeviceSitesError: (state, action) => {
			state.loadingUpdateSites = false;
			state.error = action.payload;
		},

		// ✅ Devices Helpers Actions — modal closes optimistically; flag only drives topbar.
		actionDevices: (state) => {
			state.error = null;
			state.actionDevicesLoading = true;
		},
		actionDevicesSuccess: (state) => {
			state.actionDevicesLoading = false;
		},
		actionDevicesError: (state, action) => {
			state.actionDevicesLoading = false;
			state.error = action.payload;
		},

		// ✅ Override (admin lock/unlock). Shared by ControlDeviceModal + ControlEmergencyModal. The
		// saga expands any group to its devices and posts ONE request per device, reporting each as it
		// settles — the modals stay open and render `overrideProgress` instead of closing on a verb.
		overrideDevice: (state) => {
			state.error = null;
			state.overrideDeviceLoading = true;
		},

		// Resolved targets, before any door is actioned — every row starts pending so the list has its
		// full length from the first paint. `merge` is the retry case: re-arm only the named rows and
		// leave every other row's verdict standing.
		overrideDeviceTargets: (state, action) => {
			const { actionID, rows, merge = false } = action.payload;
			state.overrideProgress.active = true;
			state.overrideProgress.actionID = actionID;
			if (!merge) {
				state.overrideProgress.rows = rows;
				// A fresh run belongs to the modal that fired it — re-attach, or the operator would
				// watch an empty side panel while the report they just asked for filled in elsewhere.
				state.overrideProgress.detached = false;
				return;
			}
			const rearmed = new Map(rows.map((r) => [r.deviceID, r]));
			state.overrideProgress.rows = state.overrideProgress.rows.map((row) =>
				rearmed.has(row.deviceID) ? rearmed.get(row.deviceID) : row
			);
		},

		// One door settled. Keyed by deviceID so a retry overwrites that row in place.
		overrideDeviceRow: (state, action) => {
			const { deviceID, status, reason = null } = action.payload;
			const row = state.overrideProgress.rows.find((r) => r.deviceID === deviceID);
			if (row) {
				row.status = status;
				row.reason = reason;
			}
		},

		overrideDeviceSuccess: (state) => {
			state.overrideDeviceLoading = false;
			state.overrideProgress.active = false;
		},
		overrideDeviceError: (state, action) => {
			state.overrideDeviceLoading = false;
			state.overrideProgress.active = false;
			state.error = action.payload;
		},

		// Signal only — no state. Fired by any surface that has just produced an access-log row (a remote
		// unlock from a screen that posts directly, rather than through the override saga) so the feeds
		// refresh through the SAME step the override saga uses. A reducer because that is how a saga is
		// addressed; it deliberately changes nothing.
		activityStale: () => {},

		// Closing the modal HANDS THE RUN OVER; it does not stop it. A group unlock fired from the
		// Emergency button keeps going after the modal is gone, and its per-door verdicts are exactly
		// what the operator needs afterwards — so the report detaches and keeps reporting in the
		// notifications popover instead of being thrown away on `afterClose`.
		//
		// Detached is one-way per run: every modal reads the flag and treats a detached run as someone
		// else's, so reopening Emergency shows a clean preview rather than the last run's report.
		detachOverrideProgress: (state) => {
			state.overrideProgress.detached = true;
		},

		// Drops the list for good — the notifications row's dismiss, and the modals' "the operator
		// changed their intent, this report no longer describes anything" reset. Doors already actioned
		// STAY actioned: this clears the report, it does not undo anything.
		clearOverrideProgress: (state) => {
			state.overrideProgress = { active: false, actionID: null, rows: [], detached: false };
		},

		// ✅ Live check for ONE device. `online: false` is an answer (asked, nothing came
		// back), so it lands in success — only a failed request reaches the error reducer.
		checkDeviceLive: (state, action) => {
			state.liveLoadingByDeviceID[action.payload.deviceID] = true;
		},
		checkDeviceLiveSuccess: (state, action) => {
			const { deviceID, online, battery, checkedAt, source } = action.payload;
			state.liveByDeviceID[deviceID] = { online, battery, checkedAt, source };
			state.liveLoadingByDeviceID[deviceID] = false;
		},
		checkDeviceLiveError: (state, action) => {
			state.liveLoadingByDeviceID[action.payload.deviceID] = false;
		},

		// ✅ Connectivity history for one device. `before` (a HeartbeatID cursor) asks for the
		// next OLDER page of records and appends; without it the read replaces.
		//
		// 🛑 `recordsRequested` is the "we have asked" mark and it is never cleared — the same
		// guard the gateway feed uses, for the same reason: a FAILED read leaves records
		// undefined and loading false, which is indistinguishable from never having asked, so
		// an effect that guards on those re-dispatches on its own error forever.
		loadDeviceHistory: (state, action) => {
			const { deviceID, before, limit } = action.payload;
			const held = historyAt(state, deviceID);
			state.historyByDeviceID[deviceID] = {
				...held,
				recordsRequested: true,
				// Remembered so a REFRESH (what a live check dispatches) re-reads the same window
				// the feed asked for. Without it the refresh falls back to the saga's default and a
				// 30-row feed silently becomes a 20-row one the moment someone checks the device.
				limit: before ? held.limit : (limit ?? held.limit),
				// A retry must clear the previous failure, or the feed keeps apologising while
				// it is busy succeeding.
				error: null,
				loading: before ? 'append' : 'replace'
			};
		},
		loadDeviceHistorySuccess: (state, action) => {
			const { deviceID, buckets, records = [], nextBefore = null, append } = action.payload;
			const held = historyAt(state, deviceID);
			state.historyByDeviceID[deviceID] = {
				loading: false,
				recordsRequested: true,
				error: null,
				// Carried through: this reducer REBUILDS the entry, so the window the feed asked
				// for has to survive the answer for the next refresh to re-read it.
				limit: held.limit,
				// An append page carries the same buckets; a paginated read must not re-write the
				// strip with a narrower window than the one on screen.
				buckets: append ? held.buckets : buckets || [],
				records: [...(append ? held.records || [] : []), ...records],
				nextBefore
			};
		},
		loadDeviceHistoryError: (state, action) => {
			const { deviceID, error } = action.payload;
			// The reason is kept, not just the fact: a feed that says "no checks yet" when the
			// request actually failed is telling the user the opposite of what happened.
			state.historyByDeviceID[deviceID] = {
				...historyAt(state, deviceID),
				loading: false,
				error: error || true
			};
		}

		// ✅ Live check for MANY devices — the saga fans out the single check with bounded
		// concurrency. Complete fires on success AND failure so the cooldown always arms;
		// a check that failed must not be instantly re-spammable either.
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const devicesActions = devicesSlice.actions;
export default devicesSlice.reducer;
